<template>
  <v-card>
    <v-card-text>
      <v-row class="mb-2">
        <v-col
          class="d-flex flex-column justify-space-between reports-charts"
          v-for="(chart, index) in charts"
          :key="index"
        >
          <div class="text-center text-overline mt-2">{{ chart.label }}</div>
          <div class="text-center nowrap">{{ chart.total }}</div>
          <div class="text-center nowrap">{{ chart.current }}</div>
          <div class="text-center nowrap">{{ chart.previous }}</div>
          <ChartPie
            :items="chart.items"
            :sizes="{ lg: 120, md: 120, sm: 120 }"
            :width="20"
            hide-summary
            hide-legend
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import colors from 'vuetify/lib/util/colors'
import { DEFAULT_REPORT, calculateBaseCurrencyCentsToChosen } from '@/helpers'

export default {
  name: 'ReportCharts',
  props: {
    report: {
      type: Object,
      default: () => ({ ...DEFAULT_REPORT })
    },
    chosenCurrency: {
      type: Object,
      default: () => ({ })
    },
    currencies: Array
  },
  data () {
    return {
      colors: ['blue', 'orange', 'green', 'yellow', 'purple']
    }
  },
  computed: {
    baseCurrency () {
      return this.currencies ? this.currencies[0] : {}
    },
    charts () {
      const variationsCharts = Object.values(this.report.topics.reduce((acc, curr) => {
        if (acc[curr.type]) {
          acc[curr.type].items[0].value += curr.monthQuantity
          acc[curr.type].items[1].value += curr.projectToDateQuantity - curr.monthQuantity
          acc[curr.type].total += curr.projectToDateQuantity
        } else {
          const index = Object.values(acc).length
          acc[curr.type] = {
            label: curr.type,
            total: curr.projectToDateQuantity,
            items: [{
              value: curr.monthQuantity,
              color: colors[this.colors[index]].darken1
            }, {
              value: curr.projectToDateQuantity - curr.monthQuantity,
              color: colors[this.colors[index]].lighten4
            }]
          }
        }
        return acc
      }, {})).map(i => ({
        ...i,
        total: this.$t('reports.topics.total', { number: i.total }),
        previous: this.$t('reports.topics.lastMonth', { number: i.items[1].value }),
        current: this.$t('reports.topics.currentMonth', {
          number: i.items[0].value,
          percent: Math.round(((i.items[0].value / (i.items[1].value || 1)) || 0) * 10000) / 100
        })
      }))
      if (this.report.liquidatedDamagesCap) {
        const capCentsBase = this.liquidatedDamagesCapCents.sum || this.liquidatedDamagesCapCents
        const capCentsChosen = calculateBaseCurrencyCentsToChosen(capCentsBase, this.baseCurrency, this.chosenCurrency)
        const liquidatedDamagesSumChosen = calculateBaseCurrencyCentsToChosen(this.report.liquidatedDamagesSum, this.baseCurrency, this.chosenCurrency)
        variationsCharts.push({
          label: this.$t('reports.topics.liquidatedDamages'),
          total: this.$t('reports.topics.total', {
            number: this.$options.filters.formatCurrency(liquidatedDamagesSumChosen, { ...this.chosenCurrency, value: 1 })
          }),
          current: this.$t('reports.topics.liquidatedDamagesCap', {
            value: this.$options.filters.formatCurrency(capCentsChosen, { ...this.chosenCurrency, value: 1 }),
            percent: this.percentOfLiquidatedDamagesCapUsed
          }),
          items: [{
            value: this.percentOfLiquidatedDamagesCapUsed,
            color: colors[this.colors[variationsCharts.length]].darken1
          }, {
            value: 100 - this.percentOfLiquidatedDamagesCapUsed,
            color: colors[this.colors[variationsCharts.length]].lighten4
          }]
        })
      }
      const adjustedPriceBaseCurrency = this.report.signedContractPriceCents.sum + this.report.adjustmentOfPriceCents.sum
      const adjustedPriceChosenCurrency = calculateBaseCurrencyCentsToChosen(adjustedPriceBaseCurrency, this.baseCurrency, this.chosenCurrency)
      const paidValueChosenCurrency = calculateBaseCurrencyCentsToChosen(this.report.paidValueCents.sum, this.baseCurrency, this.chosenCurrency)
      variationsCharts.push({
        label: this.$t('reports.topics.budget'),
        total: this.$t('reports.topics.paid', {
          number: this.$options.filters.formatCurrency(paidValueChosenCurrency, { ...this.chosenCurrency, value: 1 })
        }),
        previous: this.$t('reports.topics.totalWithPercent', {
          number: this.$options.filters.formatCurrency(adjustedPriceChosenCurrency, { ...this.chosenCurrency, value: 1 }),
          percent: Math.floor((paidValueChosenCurrency / adjustedPriceChosenCurrency) * 10000) / 100
        }),
        items: [{
          value: paidValueChosenCurrency,
          color: colors[this.colors[variationsCharts.length]].darken1
        }, {
          value: adjustedPriceChosenCurrency - paidValueChosenCurrency,
          color: colors[this.colors[variationsCharts.length]].lighten4
        }]
      })
      return variationsCharts.filter((chart) => chart.label.toLowerCase() === 'budget' || chart.label.toLowerCase() === 'liquidated damages')
    },
    liquidatedDamagesCapCents () {
      return this.report.liquidatedDamagesCap.valuePercent ? this.calculateLiquidatedDamageCapFromPercent(this.report.liquidatedDamagesCap.valuePercent, this.report.signedContractPriceCents, this.report.adjustmentOfPriceCents) : this.report.liquidatedDamagesCap.valueCents * (this.capCurrency ? this.capCurrency.value : null)
    },
    percentOfLiquidatedDamagesCapUsed () {
      if (typeof this.liquidatedDamagesCapCents === 'object') {
        return this.liquidatedDamagesCapCents.sum > 0 ? Number((this.report.liquidatedDamagesSum / this.liquidatedDamagesCapCents.sum * 100).toFixed(2)) : 100
      }
      return this.liquidatedDamagesCapCents > 0 ? Number((this.report.liquidatedDamagesSum / this.liquidatedDamagesCapCents * 100).toFixed(2)) : 100
    },
    capCurrency () {
      return this.currencies.find(i => i.key === this.report.liquidatedDamagesCap.currency)
    }
  },
  methods: {
    calculateLiquidatedDamageCapFromPercent (percent, signedContractPriceCents, adjustmentOfPriceCents) {
      const liquidatedDamagesCapCents = {}
      const keys = Object.keys(signedContractPriceCents)
      keys.forEach(key => {
        liquidatedDamagesCapCents[key] = (signedContractPriceCents[key] + adjustmentOfPriceCents[key]) * percent
      })
      return liquidatedDamagesCapCents
    }
  }
}
</script>

<style lang="scss">
$text-color: rgb(0, 0, 0);

.reports-charts {
  color: $text-color;
}
</style>
